import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, InstagramLogo } from "@phosphor-icons/react";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";

const FAQ = [
  { q: "Where are Flowtastics events held?", a: "Most events take place in Boston — rooftops, studios, beaches, galleries, and partner spaces. Each event listing shares the exact location." },
  { q: "Do I need yoga experience to attend?", a: "Not at all. All levels are welcome. Our flows are guided with care and modifications are always offered." },
  { q: "How are charity donations handled?", a: "For charity flows like Flow for a Cause, Aerial Silks and Frenchie Flow, profits go directly to our partner nonprofit. We share totals openly." },
  { q: "Can I book a private event or bachelorette?", a: "Yes! From bachelorette weekends to birthday bashes, we host fully tailored private events. Scheduling is flexible — text or email to start the conversation." },
  { q: "Do you partner with brands?", a: "We collaborate with women-owned businesses and mission-driven organizations. Reach out via the form below or DM us on Instagram." },
];

export default function Contact() {
  return (
    <div data-testid="page-contact" className="pb-10">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-28">
        <SectionLabel className="mb-8">Contact · Reach Out</SectionLabel>
        <FadeUp>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.92] tracking-tight">
            Let's<br /><span className="font-serif-italic text-teal">flow together.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 max-w-2xl font-light text-lg leading-relaxed text-charcoal/75">
          Book a private event, ask about a flow, or start a partnership — we read every message and respond personally.
        </FadeUp>
      </section>

      {/* Contact grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-20 md:mt-28 grid lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5 space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-editorial text-charcoal/55 mb-3">Email</div>
            <a href={`mailto:${CONTACT.email}`} className="font-serif text-3xl md:text-4xl hover:text-teal transition-colors break-all" data-testid="contact-email">{CONTACT.email}</a>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-editorial text-charcoal/55 mb-3">Phone / Text</div>
            <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, "")}`} className="font-serif text-3xl md:text-4xl hover:text-teal transition-colors" data-testid="contact-phone">{CONTACT.phone}</a>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-editorial text-charcoal/55 mb-3">Instagram</div>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="font-serif text-3xl md:text-4xl hover:text-teal transition-colors inline-flex items-center gap-3" data-testid="contact-instagram">
              <InstagramLogo size={28} weight="light" /> {CONTACT.instagramHandle}
            </a>
          </div>
          <div className="pt-6 border-t border-charcoal/15">
            <p className="font-serif italic text-xl text-charcoal/70">Based in Boston · Available across New England + bookings nationwide.</p>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-7">
          <ContactForm />
        </FadeUp>
      </section>

      {/* FAQ */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 mt-28 md:mt-40" data-testid="contact-faq">
        <FadeUp>
          <SectionLabel className="mb-6">FAQ</SectionLabel>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
            Questions, <span className="font-serif-italic text-teal">answered.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-14">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-charcoal/15 rounded-2xl px-6 data-[state=open]:bg-blush/20 transition-colors">
                <AccordionTrigger className="font-serif text-xl md:text-2xl text-left hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="font-light text-charcoal/75 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </section>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success("Message sent", { description: "We'll respond personally within a few days." });
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch {
      toast.error("Please check your details and try again.");
    } finally { setLoading(false); }
  };
  const subjects = ["General Inquiry", "Private Event Booking", "Partnership", "Press / Media", "Nonprofit Collaboration"];
  return (
    <form onSubmit={submit} className="bg-cream border border-charcoal/15 rounded-3xl p-6 md:p-10 space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-full border border-charcoal/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:border-teal" data-testid="contact-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-full border border-charcoal/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:border-teal" data-testid="contact-email-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-full border border-charcoal/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:border-teal" data-testid="contact-phone-input" />
        <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-full border border-charcoal/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:border-teal" data-testid="contact-subject">
          {subjects.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <textarea required rows="6" placeholder="Tell us about your idea, event, or question…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-3xl border border-charcoal/15 bg-white px-5 py-4 text-sm focus:outline-none focus:border-teal" data-testid="contact-message" />
      <button type="submit" disabled={loading} className="rounded-full bg-charcoal text-cream px-7 py-4 text-[11px] uppercase tracking-editorial hover:bg-teal transition-all disabled:opacity-60" data-testid="contact-submit">
        {loading ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
